import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { scoreAPI, resultsAPI, showAPI, classEntryAPI } from '../utils/api';
import { ArrowLeft, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

const ScoringInterface = () => {
  const { classId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [classInfo, setClassInfo] = useState(null);
  const [showId, setShowId] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [time, setTime] = useState('');
  const [knocks, setKnocks] = useState(0);
  const [status, setStatus] = useState('COMPLETED');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || !['OFFICE_STAFF', 'ORG_ADMIN', 'MASTER', 'LMASTER'].includes(user.role)) {
      toast.error('Not authorized to score');
      navigate('/dashboard');
      return;
    }
    loadData();
  }, [classId, user]);

  const loadData = async () => {
    try {
      // Find the show and class info
      const shows = await showAPI.list();
      let foundShowId = null;
      for (const show of shows.data) {
        const cls = show.classes.find(c => c.id === classId);
        if (cls) {
          setClassInfo({ ...cls, showName: show.name });
          foundShowId = show.id;
          setShowId(show.id);
          break;
        }
      }

      // Load class entries — backend orders per class draw_reversed (v248)
      if (foundShowId) {
        const { data: entryData } = await classEntryAPI.list(foundShowId, classId);
        const sorted = entryData.filter(e => e.draw_number !== null);
        setEntries(sorted);
      }

      // Load results
      const { data: resultsData } = await resultsAPI.get(classId);
      setResults(resultsData.results || []);

      // Set current index to first unscored entry
      if (resultsData.results) {
        const scoredEntryIds = new Set(resultsData.results.map(r => r.entry_id));
        const { data: entryData } = await classEntryAPI.list(foundShowId, classId);
        const sorted = entryData.filter(e => e.draw_number !== null);
        const nextIdx = sorted.findIndex(e => !scoredEntryIds.has(e.id));
        if (nextIdx >= 0) setCurrentIndex(nextIdx);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load scoring data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitScore = async () => {
    if (status === 'COMPLETED' && !time) {
      toast.error('Please enter a time');
      return;
    }
    const currentRider = entries[currentIndex];
    if (!currentRider) {
      toast.error('No current rider');
      return;
    }
    try {
      await scoreAPI.submit({
        entry_id: currentRider.id,
        class_id: classId,
        time: status === 'COMPLETED' ? parseFloat(time) : null,
        knocks: status === 'COMPLETED' ? knocks : 0,
        status
      });
      toast.success(`Score recorded for ${currentRider.rider_name}`);
      if (currentIndex < entries.length - 1) {
        setCurrentIndex(currentIndex + 1);
      }
      setTime('');
      setKnocks(0);
      setStatus('COMPLETED');
      // Reload results
      const { data: resultsData } = await resultsAPI.get(classId);
      setResults(resultsData.results || []);
    } catch (error) {
      toast.error('Failed to submit score');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="shimmer h-12 w-12 rounded-full"></div>
      </div>
    );
  }

  const currentRider = entries[currentIndex];
  const onDeck = entries[currentIndex + 1];
  const inHole = entries[currentIndex + 2];

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <nav className="bg-[#0A0A0A]/80 backdrop-blur-xl border-b border-white/5 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button
              onClick={() => showId ? navigate(`/shows/${showId}`) : navigate('/dashboard')}
              className="flex items-center gap-2 text-[#9E9E9E] hover:text-white transition-colors"
              data-testid="scoring-back-btn"
            >
              <ArrowLeft size={20} />
              <span>Back</span>
            </button>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 bg-[#00D4FF] rounded-full animate-pulse"></span>
              <span className="text-[#00D4FF] font-semibold text-sm">LIVE SCORING</span>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="mb-4 sm:mb-6">
          <h1 className="text-xl sm:text-3xl font-black text-white mb-1" style={{fontFamily: 'Outfit'}} data-testid="scoring-show-name">
            {classInfo?.showName}
          </h1>
          <p className="text-base sm:text-lg text-[#0096FF]" data-testid="scoring-class-name">{classInfo?.name}</p>
          <p className="text-xs sm:text-sm text-[#9E9E9E] mt-1">{entries.length} entries in draw &bull; {currentIndex + 1} of {entries.length}</p>
        </div>

        {entries.length === 0 ? (
          <div className="bg-[#161616] border border-[#262626] rounded-lg p-12 text-center">
            <p className="text-[#9E9E9E] text-lg">No draw generated for this class yet.</p>
            <p className="text-[#555] text-sm mt-2">Go back to the show detail page and generate a draw first.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Scoring Panel */}
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-gradient-to-r from-[#00D4FF]/20 to-[#0096FF]/10 border-2 border-[#00D4FF] rounded-lg p-4 sm:p-6">
                <div className="text-center mb-4 sm:mb-5">
                  <p className="text-[#00D4FF] text-xs font-semibold mb-1">CURRENT RIDER</p>
                  <p className="text-2xl sm:text-4xl font-black text-white mb-1" style={{fontFamily: 'Outfit'}} data-testid="current-rider-name">
                    {currentRider?.rider_name || 'Done'}
                  </p>
                  {currentRider?.horse_name && (
                    <p className="text-sm sm:text-lg text-[#9E9E9E]">on {currentRider.horse_name}</p>
                  )}
                  <p className="text-base sm:text-xl text-[#9E9E9E]">Draw #{currentRider?.draw_number}</p>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Time (seconds)</label>
                    <input
                      type="number"
                      step="0.001"
                      value={time}
                      onChange={(e) => setTime(e.target.value)}
                      disabled={status !== 'COMPLETED'}
                      className="w-full px-6 py-4 bg-[#0A0A0A] border-2 border-[#262626] rounded-md text-white text-2xl font-bold text-center focus:outline-none focus:border-[#0096FF] transition-colors disabled:opacity-50"
                      placeholder="0.000"
                      data-testid="time-input"
                      autoFocus
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#F8F8F8] mb-2">Knocks</label>
                    <div className="grid grid-cols-7 gap-1.5 sm:gap-2">
                      {[0, 1, 2, 3, 4, 5, 6].map((k) => (
                        <button
                          key={k}
                          onClick={() => setKnocks(k)}
                          disabled={status !== 'COMPLETED'}
                          data-testid={`knock-${k}`}
                          className={`px-2 py-3 sm:px-3 sm:py-2.5 rounded-md font-bold text-base sm:text-base transition-all ${
                            knocks === k
                              ? 'bg-[#FF3B30] text-white scale-105'
                              : 'bg-[#161616] border border-[#262626] text-[#9E9E9E] hover:border-[#FF3B30]'
                          } disabled:opacity-50`}
                        >
                          {k}
                        </button>
                      ))}
                    </div>
                    {knocks > 0 && status === 'COMPLETED' && (
                      <p className="text-[#FF3B30] text-sm mt-2 text-center">+{knocks * 5}s penalty</p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {[
                      { val: 'COMPLETED', label: 'Clean', color: '#34C759' },
                      { val: 'NT', label: 'NT', color: '#FACC15' },
                      { val: 'SCR', label: 'SCR', color: '#9E9E9E' },
                      { val: 'DQ', label: 'DQ', color: '#FF3B30' }
                    ].map(({ val, label, color }) => (
                      <button
                        key={val}
                        onClick={() => { setStatus(val); if (val !== 'COMPLETED') { setTime(''); setKnocks(0); } }}
                        data-testid={`status-${val.toLowerCase()}`}
                        className={`px-3 py-3 sm:py-2.5 rounded-md font-semibold text-sm flex items-center justify-center gap-1 transition-all ${
                          status === val
                            ? `text-white`
                            : 'bg-[#161616] border border-[#262626] text-[#9E9E9E]'
                        }`}
                        style={status === val ? { backgroundColor: color } : {}}
                      >
                        {val === 'COMPLETED' && <CheckCircle size={16} weight="fill" />}
                        {label}
                      </button>
                    ))}
                  </div>

                  <button
                    onClick={handleSubmitScore}
                    className="w-full px-6 py-4 bg-[#0096FF] hover:bg-[#00D4FF] text-white font-bold text-base rounded-md transition-colors"
                    data-testid="submit-score-btn"
                  >
                    Submit Score & Next Rider
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#161616] border border-[#262626] rounded-lg p-4">
                  <p className="text-[#0096FF] text-xs font-semibold mb-1">ON DECK</p>
                  <p className="text-white font-bold text-sm">{onDeck?.rider_name || 'None'}</p>
                  {onDeck && <p className="text-[#9E9E9E] text-xs">Draw #{onDeck.draw_number}</p>}
                </div>
                <div className="bg-[#161616] border border-[#262626] rounded-lg p-4">
                  <p className="text-[#00D4FF] text-xs font-semibold mb-1">IN THE HOLE</p>
                  <p className="text-white font-bold text-sm">{inHole?.rider_name || 'None'}</p>
                  {inHole && <p className="text-[#9E9E9E] text-xs">Draw #{inHole.draw_number}</p>}
                </div>
              </div>
            </div>

            {/* Live Results */}
            <div className="lg:col-span-1">
              <div className="bg-[#161616] border border-[#262626] rounded-lg p-5 sticky top-24">
                <h3 className="text-lg font-bold text-white mb-4" style={{fontFamily: 'Outfit'}}>Live Standings</h3>
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {results.length === 0 ? (
                    <p className="text-[#9E9E9E] text-center py-6 text-sm">No scores yet</p>
                  ) : (
                    results.map((result, index) => (
                      <div
                        key={result.entry_id || result.rider_name}
                        data-testid={`result-row-${index}`}
                        className={`p-3 rounded-lg ${
                          index === 0 ? 'bg-[#FFD700]/10 border border-[#FFD700]/30' :
                          index === 1 ? 'bg-[#C0C0C0]/10 border border-[#C0C0C0]/30' :
                          index === 2 ? 'bg-[#CD7F32]/10 border border-[#CD7F32]/30' :
                          'bg-[#0A0A0A]'
                        }`}
                      >
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-2">
                            <span className={`text-xl font-black ${
                              index === 0 ? 'text-[#FFD700]' :
                              index === 1 ? 'text-[#C0C0C0]' :
                              index === 2 ? 'text-[#CD7F32]' :
                              'text-[#9E9E9E]'
                            }`} style={{fontFamily: 'Outfit'}}>
                              {result.place}
                            </span>
                            <div>
                              <p className="text-white font-semibold text-sm">{result.rider_name}</p>
                              {result.knocks > 0 && <p className="text-[#9E9E9E] text-xs">{result.knocks} knocks</p>}
                            </div>
                          </div>
                          <p className="text-white font-bold" style={{fontFamily: 'JetBrains Mono'}}>
                            {result.final_time?.toFixed(3)}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScoringInterface;
